import React, { useEffect, useState,useMemo } from 'react';
import confetti from 'canvas-confetti';
import moment from 'moment';
import { Calendar, CheckCircle } from 'lucide-react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Paper, 
  Button, 
  makeStyles 
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useAggregatedServices } from '../../../utils/aggregateServices'

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  relative: {
    position: 'relative',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'opacity 300ms ease-in-out',
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'all 300ms ease-in-out',
    transform: 'scale(1)',
    opacity: 1,
  },
  checkIcon: {
    width: 80,
    height: 80,
    color: theme.palette.success.main,
    marginBottom: theme.spacing(3),
  },
  detailsPaper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    width: '100%',
    maxWidth: 500,
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  detailItem: {
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 700,
  },
}));

const Thankyou = ({ bookingData,location }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const {services,selectedTime,customerAddress}=bookingData;
  const aggregatedActivities = useAggregatedServices(services)
  const meetingLocation = customerAddress || location

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
      setShowSuccess(true);
      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const startTime = useMemo(()=>moment(selectedTime).format("YYYYMMDD[T]HHmmss"),[selectedTime]);
  const endTime = useMemo(()=>moment(selectedTime).add(aggregatedActivities.duration, 'minutes').format("YYYYMMDD[T]HHmmss"),[selectedTime,aggregatedActivities.duration]);
  

  const eventDetails = {
    title: aggregatedActivities.subProfessionId.heName,
    start: startTime,
    end: endTime,
    ...(meetingLocation && { location: meetingLocation.title}),
    description: t('thankyou_calendar_description', { service: aggregatedActivities.subProfessionId.heName })
  };

  const handleAddToCalendar = (type) => {
    if (type === 'google') {
      const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventDetails.title)}&dates=${eventDetails.start}/${eventDetails.end}${eventDetails?.location?`&location=${encodeURIComponent(eventDetails?.location)}`:''}&details=${encodeURIComponent(eventDetails.description)}`;
      window.open(googleUrl, '_blank');
    } else {
      // Apple Calendar
      const appleUrl = `data:text/calendar;charset=utf-8,BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
URL:${document.URL}
DTSTART:${eventDetails.start}
DTEND:${eventDetails.end}
SUMMARY:${eventDetails.title}
DESCRIPTION:${eventDetails.description}
${eventDetails?.location?`LOCATION:${eventDetails?.location}`:''}
END:VEVENT
END:VCALENDAR`;
      
      const link = document.createElement('a');
      link.href = appleUrl;
      link.setAttribute('download', 'appointment.ics');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.relative}>
        {isLoading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={64} thickness={4} />
            <Typography variant="h6" color="textSecondary" style={{ marginTop: 16 }}>
              {t('thankyou_loading')}
            </Typography>
          </Box>
        )}

        {showSuccess && (
          <Box className={classes.successContainer}>
            <CheckCircle className={classes.checkIcon} />
            <Typography variant="h4" gutterBottom>
              {t('thankyou_success_title')}
            </Typography>
            
            <Paper className={classes.detailsPaper} elevation={3}>

              <Box>
                <Typography className={classes.detailItem}>
                  <span className={classes.label}>{t('thankyou_details_service')}:</span> {eventDetails.title}
                </Typography>
                <Typography className={classes.detailItem}>
                  <span className={classes.label}>{t('thankyou_details_date')}:</span> {moment(selectedTime).format('DD.MM.YYYY')}
                </Typography>
                <Typography className={classes.detailItem}>
                  <span className={classes.label}>{t('thankyou_details_time')}:</span> {moment(selectedTime).format('h:mm A')}
                </Typography>
                <Typography className={classes.detailItem}>
                  <span className={classes.label}>{t('thankyou_details_duration')}:</span> {aggregatedActivities.duration} {t('thankyou_details_minutes')}
                </Typography>
                {eventDetails.location && <Typography className={classes.detailItem}>
                  <span className={classes.label}>{t('thankyou_details_location')}:</span> {eventDetails.location}
                </Typography>}
              </Box>
            </Paper>

            <Box className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Calendar />}
                onClick={() => handleAddToCalendar('google')}
              >
                {t('thankyou_calendar_google')}
              </Button>
              <Button
                variant="contained"
                color="default"
                startIcon={<Calendar />}
                onClick={() => handleAddToCalendar('apple')}
                style={{ backgroundColor: '#424242', color: 'white' }}
              >
                {t('thankyou_calendar_apple')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Thankyou;
